import { useEffect, useState } from 'react';
import { predefiniti } from '../../api/predefiniti';
import type { ParametriLetti, PredefinitiLetti } from '../../api/predefiniti';
import { formattaPercentuale } from '../../lib/formato';
import { numeroDecimale } from '../../lib/numeri';
import { usePuo } from '../sessione/ContestoSessione';

/*  I VALORI CON CUI NASCE UN PREVENTIVO, categoria di cliente per categoria di cliente.

    Chiesto da Andrea il 28/09/2026 guardando un'offerta appena aperta: ricarico zero,
    costi generali zero, «Valido fino al» vuoto. Un preventivo che nasce così vende a
    costo finché qualcuno non se ne accorge, e chi se ne accorge è chi legge il margine
    a fine trimestre.

    LA CATEGORIA è quella di Mago — Ditte, Privati, Condomini, Enti pubblici, Edili,
    Partecipate, Gestione calore — la stessa che il preventivo si prende dal cliente. È
    proprio la distinzione che serve: su un ente pubblico il margine è un altro, e la
    validità di un'offerta a un condominio non è quella di un'offerta a un'impresa.

    Due permessi, come per i testi d'offerta. Leggere serve a chi compila: «perché questo
    nasce col 18 e quello col 25» è una domanda a cui deve poter rispondere da solo, e
    senza questa schermata l'unica risposta era chiedere a noi. Scrivere no.

    SONO VALORI DI PARTENZA. Cambiarli qui non tocca un preventivo già scritto: quello ha
    i suoi numeri sopra e non li rilegge mai più. Detto nella schermata e non solo nel
    codice, perché è la prima cosa che qualcuno teme di aver fatto premendo «salva».
*/
export function PredefinitiPreventivo() {
  const modificabili = usePuo()('GestisciPredefinitiPreventivo');

  const [righe, setRighe] = useState<PredefinitiLetti[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const rileggi = () =>
    predefiniti
      .elenco()
      .then(setRighe)
      .catch((e: Error) => setErrore(e.message));

  useEffect(() => {
    void rileggi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*  Dopo ogni scrittura si rilegge TUTTO e non solo la riga toccata: cambiare «tutti
      gli altri» cambia anche i numeri mostrati da ogni categoria che eredita, e
      aggiornare la sola riga salvata lascerebbe le altre a dire il valore di prima —
      cioè a mentire, in silenzio, proprio sulla schermata dove si controllano.  */
  const dopoAverScritto = (azione: Promise<unknown>) => {
    setInCorso(true);
    setErrore(null);

    return azione
      .then(rileggi)
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(false));
  };

  if (righe === null)
    return (
      <section className="predefiniti">
        <h1>Valori di partenza dell’offerta</h1>
        {errore === null ? <p className="nota">Un momento…</p> : <p className="errore">{errore}</p>}
      </section>
    );

  return (
    <section className="predefiniti">
      <h1>Valori di partenza dell’offerta</h1>

      <p className="nota">
        Con questi numeri nasce un preventivo nuovo, secondo la categoria del cliente in
        Mago. Chi compila li può sempre cambiare sul singolo documento, e{' '}
        <strong>cambiarli qui non tocca le offerte già scritte</strong>: quelle hanno i
        loro numeri sopra e non li rileggono più.
      </p>

      {!modificabili && (
        <p className="nota">
          Li puoi leggere ma non cambiare: spostano il margine di tutte le offerte che
          verranno, non di quella che hai davanti.
        </p>
      )}

      {errore !== null && <p className="errore">{errore}</p>}

      <ParametriAziendali modificabili={modificabili} />

      <h2>Per categoria di cliente</h2>

      <table className="confronto predefiniti-tabella">
        <thead>
          <tr>
            <th>Categoria</th>
            <th className="destra" title="Il ricarico generale del preventivo">Ricarico</th>
            <th className="destra" title="Percentuale sul costo diretto più le accessorie">
              Costi generali
            </th>
            <th className="destra" title="Da oggi a «Valido fino al». Zero: l’offerta non scade">
              Validità
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {righe.map((riga) => (
            <Riga
              key={riga.codiceCategoria === '' ? '(generale)' : riga.codiceCategoria}
              riga={riga}
              modificabile={modificabili}
              inCorso={inCorso}
              onScrivi={(valori) =>
                dopoAverScritto(predefiniti.imposta(riga.codiceCategoria, valori))
              }
              onEredita={() => dopoAverScritto(predefiniti.rimuovi(riga.codiceCategoria))}
            />
          ))}
        </tbody>
      </table>
    </section>
  );
}

/*  Una riga: si legge sempre, si corregge dove si può.

    Il bottone di salvataggio compare solo quando qualcosa è cambiato — come sulle righe
    del preventivo, e per lo stesso motivo: un «salva» sempre acceso invita a premerlo
    per sicurezza, e il salvataggio che non salva niente è il modo migliore per non
    fidarsi più di quello che si vede.
*/
function Riga({
  riga,
  modificabile,
  inCorso,
  onScrivi,
  onEredita,
}: {
  riga: PredefinitiLetti;
  modificabile: boolean;
  inCorso: boolean;
  onScrivi: (valori: {
    ricaricoPercentuale: number;
    costiGeneraliPercentuale: number;
    giorniValidita: number;
  }) => void;
  onEredita: () => void;
}) {
  const [ricarico, setRicarico] = useState(String(riga.ricaricoPercentuale));
  const [generali, setGenerali] = useState(String(riga.costiGeneraliPercentuale));
  const [giorni, setGiorni] = useState(String(riga.giorniValidita));

  /*  Quando la riletta cambia i valori si riallineano: dopo aver toccato «tutti gli
      altri», le righe che ereditano mostrano numeri nuovi che nessuno ha battuto qui.  */
  useEffect(() => {
    setRicarico(String(riga.ricaricoPercentuale));
    setGenerali(String(riga.costiGeneraliPercentuale));
    setGiorni(String(riga.giorniValidita));
  }, [riga.ricaricoPercentuale, riga.costiGeneraliPercentuale, riga.giorniValidita]);

  const valori = {
    ricaricoPercentuale: numeroDecimale(ricarico) ?? 0,
    costiGeneraliPercentuale: numeroDecimale(generali) ?? 0,
    giorniValidita: Math.trunc(numeroDecimale(giorni) ?? 0),
  };

  const cambiata =
    valori.ricaricoPercentuale !== riga.ricaricoPercentuale ||
    valori.costiGeneraliPercentuale !== riga.costiGeneraliPercentuale ||
    valori.giorniValidita !== riga.giorniValidita;

  /*  Su una riga che EREDITA il bottone c'è sempre, anche senza aver toccato niente:
      scrivere gli stessi numeri è una decisione — fissa per questa categoria quello che
      finora seguiva la generale, e da domani non la segue più. Ma si chiama «Fissa qui»
      e non «Salva», o sei righe con un bottone acceso sembrerebbero sei righe con delle
      modifiche in sospeso.  */
  const azione = riga.propri ? (cambiata ? 'Salva' : null) : cambiata ? 'Salva qui' : 'Fissa qui';

  return (
    <tr className={riga.codiceCategoria === '' ? 'generale' : undefined}>
      <td>
        <span className="nome">{riga.categoria}</span>
        {!riga.propri && riga.codiceCategoria !== '' && (
          <em className="eredita"> segue «Tutti gli altri»</em>
        )}
      </td>

      {modificabile ? (
        <>
          <td className="destra">
            <input
              className="numero-riga"
              inputMode="decimal"
              aria-label={`Ricarico di ${riga.categoria}`}
              value={ricarico}
              onChange={(e) => setRicarico(e.target.value)}
            />
          </td>
          <td className="destra">
            <input
              className="numero-riga"
              inputMode="decimal"
              aria-label={`Costi generali di ${riga.categoria}`}
              value={generali}
              onChange={(e) => setGenerali(e.target.value)}
            />
          </td>
          <td className="destra">
            <input
              className="numero-riga"
              inputMode="numeric"
              aria-label={`Giorni di validità di ${riga.categoria}`}
              value={giorni}
              onChange={(e) => setGiorni(e.target.value)}
            />
          </td>
        </>
      ) : (
        <>
          <td className="destra">{formattaPercentuale(riga.ricaricoPercentuale)}</td>
          <td className="destra">{formattaPercentuale(riga.costiGeneraliPercentuale)}</td>
          <td className="destra">
            {riga.giorniValidita === 0 ? 'non scade' : `${riga.giorniValidita} gg`}
          </td>
        </>
      )}

      <td className="comandi">
        {modificabile && azione !== null && (
          <button
            type="button"
            className={riga.propri ? undefined : 'tenue'}
            disabled={inCorso}
            title={
              riga.propri
                ? undefined
                : 'Fissa questi valori per la categoria: smette di seguire «Tutti gli altri»'
            }
            onClick={() => onScrivi(valori)}
          >
            {azione}
          </button>
        )}
        {/*  Tornare a ereditare non si offre sulla generale — è il fondo della catena,
             e senza di lei una categoria senza riga tornerebbe a zero, cioè al difetto
             per cui questa tabella esiste. Per non ricaricare niente ci si scrive zero,
             che è una decisione.  */}
        {modificabile && riga.propri && riga.codiceCategoria !== '' && (
          <button
            type="button"
            className="tenue"
            disabled={inCorso}
            title="Toglie i valori suoi: torna a seguire «Tutti gli altri»"
            onClick={onEredita}
          >
            Segui la generale
          </button>
        )}
      </td>
    </tr>
  );
}


/*  I VALORI UGUALI PER TUTTI I CLIENTI, sopra la tabella delle categorie.

    Chiesto da Andrea il 01/10/2026, dopo la demo da Galizzi: «tutti i valori di default
    scriviamoli in una tabella di parametri, in modo tale che il responsabile del cliente
    possa cambiarli se ha necessità di farlo, e gli altri di default se li trovano già
    compilati nel preventivo».

    Fino a oggi stavano in `appsettings.json`. La differenza non è dove, è CHI: un numero
    in un file di configurazione lo cambia chi installa, cioè noi, dopo una telefonata e
    un riavvio. In riunione il rimborso chilometrico è passato da 0,50 a 1,00 in tre
    minuti di discussione, e il prezzo del pranzo da 12 a 15 — a quel ritmo, un parametro
    che richiede noi è un parametro che resta sbagliato.

    SOPRA e non sotto la tabella delle categorie: sono i numeri che valgono sempre, e
    quelli per categoria si leggono come eccezioni a questi.

    Un salvataggio solo per tutto il blocco, e non uno per campo: questi numeri si
    guardano insieme — il pranzo accanto alla cena, i chilometri accanto alla soglia — e
    si correggono in una seduta sola, quando qualcuno torna da una riunione con i valori
    nuovi.
*/
function ParametriAziendali({ modificabili }: { modificabili: boolean }) {
  const [letti, setLetti] = useState<ParametriLetti | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [salvato, setSalvato] = useState(false);

  const [campi, setCampi] = useState<Campi>(vuoti);

  const rileggi = () =>
    predefiniti
      .parametri()
      .then((p) => {
        setLetti(p);
        setCampi(dalServer(p));
      })
      .catch((e: Error) => setErrore(e.message));

  useEffect(() => {
    void rileggi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (letti === null)
    return errore === null ? (
      <p className="nota">Un momento…</p>
    ) : (
      <p className="errore">{errore}</p>
    );

  const scrivi = (nome: keyof Campi) => (e: { target: { value: string } }) => {
    setCampi({ ...campi, [nome]: e.target.value });
    setSalvato(false);
  };

  /*  Un campo vuoto vale zero e non «lascia stare»: qui non c'è niente da lasciar
      calcolare — un costo della cena vuoto è una cena che non si paga, ed è una
      decisione come un'altra. L'unica eccezione è la voce dei materiali nascosti, dove
      vuoto vuol dire «rimetti quella di sempre», perché una riga senza nome in offerta è
      peggio di una riga scritta da noi.  */
  const salva = () => {
    setInCorso(true);
    setErrore(null);

    predefiniti
      .impostaParametri({
        tariffaManodoperaOraria: numeroDecimale(campi.tariffaOraria) ?? 0,
        tariffaKm: numeroDecimale(campi.tariffaKm) ?? 0,
        indennitaTrasfertaGiornaliera: numeroDecimale(campi.indennita) ?? 0,
        kmPerIlPranzo: numeroDecimale(campi.kmPranzo) ?? 0,
        kmPerIlPernotto: numeroDecimale(campi.kmPernotto) ?? 0,
        costoPranzo: numeroDecimale(campi.costoPranzo) ?? 0,
        costoCena: numeroDecimale(campi.costoCena) ?? 0,
        costoPernottamento: numeroDecimale(campi.costoNotte) ?? 0,
        percentualeSicurezza: numeroDecimale(campi.sicurezza) ?? 0,
        orePerGiornata: numeroDecimale(campi.orePerGiornata) ?? 0,
        voceMaterialiNascosti: campi.voceNascoste.trim() || null,
      })
      .then((p) => {
        setLetti(p);
        setCampi(dalServer(p));
        setSalvato(true);
      })
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(false));
  };

  const cambiato = JSON.stringify(campi) !== JSON.stringify(dalServer(letti));

  return (
    <section className="parametri-azienda">
      <h2>Uguali per tutti i clienti</h2>

      {errore !== null && <p className="errore">{errore}</p>}

      <div className="fila numeri">
        <Campo nome="Tariffa oraria €" valore={campi.tariffaOraria} onCambia={scrivi('tariffaOraria')} modificabile={modificabili} aiuto="È già netta: non prende il ricarico." />
        <Campo nome="Costo al km €" valore={campi.tariffaKm} onCambia={scrivi('tariffaKm')} modificabile={modificabili} />
        <Campo nome="Indennità giorno €" valore={campi.indennita} onCambia={scrivi('indennita')} modificabile={modificabili} aiuto="Per ogni giornata uomo fuori." />
        <Campo nome="Ore per giornata" valore={campi.orePerGiornata} onCambia={scrivi('orePerGiornata')} modificabile={modificabili} aiuto="Da qui escono i giorni, e quindi i viaggi." />
        <Campo nome="Oneri sicurezza %" valore={campi.sicurezza} onCambia={scrivi('sicurezza')} modificabile={modificabili} aiuto="Zero: la voce non nasce." />
      </div>

      <div className="fila numeri">
        <Campo nome="Pranzo €" valore={campi.costoPranzo} onCambia={scrivi('costoPranzo')} modificabile={modificabili} />
        <Campo nome="Cena €" valore={campi.costoCena} onCambia={scrivi('costoCena')} modificabile={modificabili} />
        <Campo nome="Albergo €/notte" valore={campi.costoNotte} onCambia={scrivi('costoNotte')} modificabile={modificabili} />
        <Campo nome="Km per il pranzo" valore={campi.kmPranzo} onCambia={scrivi('kmPranzo')} modificabile={modificabili} aiuto="Distanza sola andata oltre cui si pranza fuori." />
        <Campo nome="Km per il pernotto" valore={campi.kmPernotto} onCambia={scrivi('kmPernotto')} modificabile={modificabili} aiuto="Distanza sola andata oltre cui si dorme fuori." />
      </div>

      <label className="voce-nascoste">
        <span>Voce dei materiali che non si elencano</span>
        <input
          value={campi.voceNascoste}
          onChange={scrivi('voceNascoste')}
          disabled={!modificabili}
          maxLength={200}
        />
        <em className="aiuto">
          Come si chiama in offerta quello che si sceglie di non far vedere riga per riga.
          Vuota, torna «{'Manodopera e minuteria'}».
        </em>
      </label>

      {modificabili && (
        <p className="comandi">
          <button type="button" onClick={salva} disabled={inCorso || !cambiato}>
            {inCorso ? 'Salvo…' : 'Salva i parametri'}
          </button>
          {salvato && !cambiato && (
            <em className="nota"> Salvati. Valgono per le offerte che nasceranno.</em>
          )}
        </p>
      )}
    </section>
  );
}

/*  I campi a schermo: tutto testo, perché è quello che una casella contiene davvero, e
    perché il confronto «è cambiato qualcosa» fatto su numeri formattati direbbe di sì a
    ogni «1,0» battuto al posto di «1». Un tipo con le chiavi scritte e non un
    `Record<string, string>`: così una chiave sbagliata non compila, invece di lasciare
    una casella vuota a schermo e un parametro azzerato nel salvataggio.  */
interface Campi {
  tariffaOraria: string;
  tariffaKm: string;
  indennita: string;
  kmPranzo: string;
  kmPernotto: string;
  costoPranzo: string;
  costoCena: string;
  costoNotte: string;
  sicurezza: string;
  orePerGiornata: string;
  voceNascoste: string;
}

const vuoti: Campi = {
  tariffaOraria: '',
  tariffaKm: '',
  indennita: '',
  kmPranzo: '',
  kmPernotto: '',
  costoPranzo: '',
  costoCena: '',
  costoNotte: '',
  sicurezza: '',
  orePerGiornata: '',
  voceNascoste: '',
};

function dalServer(p: ParametriLetti): Campi {
  const n = (valore: number) => String(valore).replace('.', ',');

  return {
    tariffaOraria: n(p.tariffaManodoperaOraria),
    tariffaKm: n(p.tariffaKm),
    indennita: n(p.indennitaTrasfertaGiornaliera),
    kmPranzo: n(p.kmPerIlPranzo),
    kmPernotto: n(p.kmPerIlPernotto),
    costoPranzo: n(p.costoPranzo),
    costoCena: n(p.costoCena),
    costoNotte: n(p.costoPernottamento),
    sicurezza: n(p.percentualeSicurezza),
    orePerGiornata: n(p.orePerGiornata),
    voceNascoste: p.voceMaterialiNascosti,
  };
}

function Campo({
  nome,
  valore,
  onCambia,
  modificabile,
  aiuto,
}: {
  nome: string;
  valore: string;
  onCambia: (e: { target: { value: string } }) => void;
  modificabile: boolean;
  aiuto?: string;
}) {
  return (
    <label className="stretto">
      <span>{nome}</span>
      <input
        className="campo"
        inputMode="decimal"
        value={valore}
        onChange={onCambia}
        disabled={!modificabile}
      />
      {aiuto !== undefined && <em className="aiuto">{aiuto}</em>}
    </label>
  );
}
